import React, {useState, useEffect, useRef} from 'react';
import {Robo} from './Robo.tsx';
import ReactMarkdown from 'react-markdown';

import {CircularProgress, IconButton, TextField} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import {configApiRef, useApi} from "@backstage/core-plugin-api";
import {alpha, makeStyles} from "@material-ui/core/styles";

const useStyles = makeStyles(theme => ({
    chatbotButtonWrapper: {
        position: 'fixed',
        bottom: 20,
        right: 8,
        zIndex: 1000,
    },
    roboIconContainer: {
        width: 'auto',
        height: '120px',
        padding: '10px',
        cursor: 'pointer',
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'all 0.3s ease-in-out',
        zIndex: 1000,
        position: 'relative',
        overflow: 'hidden',
    },
    chatWindow: {
        position: 'fixed',
        bottom: 25,
        right: 76,
        width: 350,
        height: 420,
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        borderRadius: 10,
        boxShadow: '0 0 10px rgba(0,0,0,0.3)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
    },
    chatHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        borderBottom: '1px solid #eee',
        color: 'white',
        backgroundColor: theme.palette.background.default,
        borderRadius: '10px 10px 0 0',
    },
    chatHeaderTitle: {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 900,
        color: "darkgray",
        fontSize: "15pt",
    },
    chatMessages: {
        padding: 12,
        flex: 1,
        overflowY: 'auto',
        color: 'white',
        fontSize: 14,
        scrollBehavior: 'smooth',
    },
    chatInputArea: {
        display: 'flex',
        borderTop: '1px solid #555',
        padding: 8,
        backgroundColor: theme.palette.background.default,
        borderRadius: '0 0 10px 10px',
    },
    userMessage: {
        marginBottom: 8,
        display: 'flex',
        justifyContent: 'flex-end',
    },
    botMessage: {
        marginBottom: 8,
        display: 'flex',
        justifyContent: 'flex-start',
    },
    messageBubbleUser: {
        background: '#1976d2',
        padding: '8px 12px 0px',
        borderRadius: 8,
        maxWidth: '90%',
        '& p': {
            margin: '0 0 8px 0',
            whiteSpace: 'pre-wrap',
        },
        '& pre': {
            background: '#333',
            padding: '8px',
            borderRadius: 4,
            overflowX: 'auto',
            fontSize: '0.9em',
            fontFamily: 'monospace',
            color: '#fff',
        },
        '& code': {
            background: '#444',
            padding: '2px 4px',
            borderRadius: 4,
            fontFamily: 'monospace',
        }
    },
    messageBubbleBot: {
        background: '#555',
        padding: '8px 12px 0px',
        borderRadius: 8,
        maxWidth: '90%',
        '& p': {
            margin: '0 0 8px 0',
            whiteSpace: 'pre-wrap',
        },
        '& pre': {
            background: '#333',
            padding: '8px',
            borderRadius: 4,
            overflowX: 'auto',
            fontSize: '0.9em',
            fontFamily: 'monospace',
            color: '#fff',
        },
        '& code': {
            background: '#444',
            padding: '2px 4px',
            borderRadius: 4,
            fontFamily: 'monospace',
        }
    },
    inputField: {
        backgroundColor: '#333333',
        borderRadius: 4,
    }
}));

export const PequiChatbotWidget = () => {
    const configApi = useApi(configApiRef);
    const logAgentBackendBaseUrl = configApi.getString('logAgentBackend.baseUrl');
    const classes = useStyles();
    const chatWindowRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<{ text: string, isUser: boolean, displayedText?: string }[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [typing, setTyping] = useState(false);

    const urlChatbot = logAgentBackendBaseUrl + 'chat';

    const roboNome = 'Pequino';
    const messageDefaultRobo = `Olá! Sou o Pequino, seu assistente de IA, como posso te ajudar?`;

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (chatWindowRef.current && !chatWindowRef.current.contains(event.target as Node) &&
                // @ts-ignore
                !event.target!.closest('.roboIconContainer')) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const animateText = (text: string, messageIndex: number) => {
        let currentIndex = 0;
        setTyping(true);

        const animate = () => {
            if (currentIndex <= text.length) {
                setMessages(prev =>
                    prev.map((msg, idx) =>
                        idx === messageIndex
                            ? {...msg, displayedText: text.substring(0, currentIndex)}
                            : msg
                    )
                );
                currentIndex++;
                requestAnimationFrame(animate);
            } else {
                setTyping(false);
            }
        };

        requestAnimationFrame(animate);
    };

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim()) return;
        const mensagem = input.trim();
        setInput('');

        setIsLoading(true);
        setMessages(prev => [...prev, {text: mensagem, isUser: true}]);

        try {
            const response = await fetch(urlChatbot, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({mensagem})
            });

            const data = await response.json();
            const newMessageIndex = messages.length + 1;
            setMessages(prev => [...prev, {text: data.resposta, isUser: false, displayedText: ''}]);
            animateText(data.resposta, newMessageIndex);
        } catch (error) {
            console.error('Error:', error);
        }

        setIsLoading(false);
    };

    const showMessage = (index: number, msg: { text: string; isUser: boolean; displayedText?: string }) => (
        <div
            key={index}
            className={msg.isUser ? classes.userMessage : classes.botMessage}
        >
            <div className={msg.isUser ? classes.messageBubbleUser : classes.messageBubbleBot}>
                <ReactMarkdown skipHtml>
                    {msg.isUser ? msg.text : (msg.displayedText || msg.text)}
                </ReactMarkdown>
            </div>
        </div>
    );

    return (
        <>
            <div className={classes.chatbotButtonWrapper}>
                <div
                    className={`${classes.roboIconContainer} roboIconContainer`}
                    onClick={() => setOpen(!open)}
                >
                    <Robo/>
                </div>
            </div>

            {open && (
                <div ref={chatWindowRef} className={classes.chatWindow}>
                    <div className={classes.chatHeader}>
                        <span className={classes.chatHeaderTitle}>{roboNome}</span>
                        <IconButton
                            size="small"
                            onClick={() => setOpen(false)}
                            aria-label="Fechar"
                            sx={{color: 'white'}}
                        >
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </div>

                    <div className={classes.chatMessages}>
                        {messages.length === 0 ? (
                            showMessage(0, {
                                text: messageDefaultRobo,
                                isUser: false,
                            })
                        ) : (
                            messages.map((msg, index) => showMessage(index, msg))
                        )}
                        <div ref={messagesEndRef}/>
                    </div>

                    <form onSubmit={handleSend} className={classes.chatInputArea}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder="Pergunte alguma coisa"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            disabled={isLoading || typing}
                            InputProps={{
                                className: classes.inputField,
                            }}
                        />
                        <IconButton
                            type="submit"
                            color="primary"
                            sx={{marginLeft: 1}}
                            disabled={!input.trim() || isLoading || typing}
                        >
                            {isLoading ? <CircularProgress size={24}/> : <SendIcon/>}
                        </IconButton>
                    </form>
                </div>
            )}
        </>
    );
};